/**
 * Wording and look that depend on the kind of business (content.industry).
 * Detailing studios book cars into boxes; beauty salons book clients with masters.
 * Studios published before the field existed have no industry: they are detailing.
 */
import type { Industry } from '@shared/business.ts';
import { useTenant } from '@/app/tenant-context.ts';

export interface IndustryCopy {
  /** Ask for the car in the booking form and show it in bookings. */
  hasCar: boolean;
  /** Light theme (beauty) or the black one (detailing). */
  mode: 'light' | 'dark';
  blockAction: string;
  blockedTitle: string;
  blockHint: string;
  blockPick: string;
  blockFill: string;
  blockTaken: string;
  pickResource: string;
  slotsByResource: string;
  resourcesHint: string;
  newResource: string;
  newResourcePlaceholder: string;
  hoursHint: string;
  bufferLabel: string;
  arrivals: string;
  arrivalsHint: string;
  weekQuestion: string;
  consent: string;
  /** «студию» / «салон» (Запись в …, позвоните в …). */
  placeAcc: string;
  /** «студии» / «салона» (часовой пояс …). */
  placeGen: string;
  /** «студии» / «салоне» (О …). */
  placePrep: string;
  closedDay: string;
  noServices: string;
}

const COPY: Record<Industry, IndustryCopy> = {
  detailing: {
    hasCar: true,
    mode: 'dark',
    blockAction: 'Закрыть бокс',
    blockedTitle: 'Закрытые боксы',
    blockHint: 'На это время клиенты не смогут записаться в выбранный бокс.',
    blockPick: 'Выберите бокс',
    blockFill: 'Заполните бокс и время.',
    blockTaken: 'На это время в боксе есть записи. Перенесите их или выберите другое время.',
    pickResource: 'Выберите хотя бы один бокс.',
    slotsByResource: 'Сначала выберите услугу — покажем свободное время по боксам.',
    resourcesHint: 'Бокс или пост — место, которое занимает машина. Каждая услуга привязана к подходящим боксам.',
    newResource: 'Новый бокс',
    newResourcePlaceholder: 'Бокс 3',
    hoursHint: 'Часы — это время приёма машин. Многодневные работы продолжаются и в нерабочие часы.',
    bufferLabel: 'Подготовка после машины, мин',
    arrivals: 'Заезды',
    arrivalsHint: 'машины, принятые в работу',
    weekQuestion: 'Сколько машин было на неделе?',
    consent: 'Нажимая кнопку, вы передаёте студии имя, телефон и автомобиль для записи.',
    placeAcc: 'студию',
    placeGen: 'студии',
    placePrep: 'студии',
    closedDay: 'В этот день студия не работает',
    noServices: 'Студия ещё не добавила услуги для онлайн-записи.',
  },
  beauty: {
    hasCar: false,
    mode: 'light',
    blockAction: 'Закрыть время мастера',
    blockedTitle: 'Закрытое время мастеров',
    blockHint: 'На это время клиенты не смогут записаться к выбранному мастеру.',
    blockPick: 'Выберите мастера',
    blockFill: 'Выберите мастера и время.',
    blockTaken: 'На это время у мастера есть записи. Перенесите их или выберите другое время.',
    pickResource: 'Выберите хотя бы одного мастера.',
    slotsByResource: 'Сначала выберите услугу — покажем свободное время мастеров.',
    resourcesHint: 'Мастер или рабочее место. Каждая услуга привязана к мастерам, которые её делают.',
    newResource: 'Новый мастер',
    newResourcePlaceholder: 'Анна',
    hoursHint: 'Часы — это время приёма клиентов.',
    bufferLabel: 'Перерыв после клиента, мин',
    arrivals: 'Визиты',
    arrivalsHint: 'клиенты, пришедшие на услугу',
    weekQuestion: 'Сколько клиентов было на неделе?',
    consent: 'Нажимая кнопку, вы передаёте салону имя и телефон для записи.',
    placeAcc: 'салон',
    placeGen: 'салона',
    placePrep: 'салоне',
    closedDay: 'В этот день салон не работает',
    noServices: 'Салон ещё не добавил услуги для онлайн-записи.',
  },
};

export function industryOf(content: { industry?: Industry } | null | undefined): Industry {
  return content?.industry === 'beauty' ? 'beauty' : 'detailing';
}

export function industryCopy(content: { industry?: Industry } | null | undefined): IndustryCopy {
  return COPY[industryOf(content)];
}

/** Mode for screens shown before the tenant loads, taken from the shell's <meta name="color-scheme">. */
export function shellMode(): 'light' | 'dark' {
  if (typeof document === 'undefined') return 'dark';
  return document.querySelector('meta[name="color-scheme"]')?.getAttribute('content') === 'light' ? 'light' : 'dark';
}

/** Wording for the current studio (inside TenantRoot). */
export function useIndustry(): IndustryCopy {
  return industryCopy(useTenant().tenant.content);
}
